import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, MapPin, UserCheck, Shield, ShieldCheck, Smartphone } from 'lucide-react';
import { toast } from '../ui/sonner';
import { Capacitor } from '@capacitor/core';

// Logo Pemda Kabupaten Asmat
const PEMDA_LOGO_URL = 'https://raw.githubusercontent.com/2516farisrchmat/aset-web/main/Lambang_Kabupaten_Asmat__1_-removebg-preview.png';

// Cloudflare Turnstile Configuration
const TURNSTILE_SITE_KEY = '0x4AAAAAACaoB8dUGudQoqOi';

// Turnstile does not work in native WebView (Capacitor Android/iOS).
// On native, we skip it entirely — security is handled by anti-mock GPS + face verification.
const IS_NATIVE_APP = Capacitor.isNativePlatform();

const LoginPage = () => {
  const [nip, setNip] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  
  const { login, isAuthenticated, isOnline } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Load Turnstile script (skip on native — Turnstile doesn't work in WebView)
  useEffect(() => {
    if (IS_NATIVE_APP) return;
    if (document.getElementById('cf-turnstile-script')) {
      if (window.turnstile) setTurnstileLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'cf-turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit';
    script.async = true;
    window.onTurnstileLoad = () => setTurnstileLoaded(true);
    script.onerror = () => console.warn('[Turnstile] Script failed to load');
    document.head.appendChild(script);
  }, []);

  // Render Turnstile widget when script is loaded (skip on native)
  const renderTurnstile = useCallback(() => {
    if (IS_NATIVE_APP) return;
    if (!turnstileLoaded || !window.turnstile || !turnstileRef.current) return;
    // Clean up previous widget
    if (turnstileWidgetId.current !== null) {
      try { window.turnstile.remove(turnstileWidgetId.current); } catch {}
    }
    try {
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        'error-callback': () => {
          console.warn('[Turnstile] Challenge error');
          setTurnstileToken(null);
        },
        'expired-callback': () => setTurnstileToken(null),
        theme: 'light',
        size: 'flexible',
      });
    } catch (e) {
      console.warn('[Turnstile] Render failed:', e.message);
    }
  }, [turnstileLoaded]);

  useEffect(() => { renderTurnstile(); }, [renderTurnstile]);

  const resetTurnstile = () => {
    setTurnstileToken(null);
    if (window.turnstile && turnstileWidgetId.current !== null) {
      try { window.turnstile.reset(turnstileWidgetId.current); } catch {}
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // On native, skip turnstile token — backend soft mode allows it
      await login(nip, password, IS_NATIVE_APP ? null : turnstileToken);
      toast.success('Login berhasil!');
      navigate('/dashboard');
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Login gagal';
      setError(message);
      toast.error(message);
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page min-h-screen">
      {/* Hero Section */}
      <div className="login-hero hidden lg:flex">
        <div className="max-w-lg">
          {/* Logo Pemda + App Logo */}
          <div className="flex flex-col items-center gap-4 mb-8 w-full">
            {/* Logo Pemda Kabupaten Asmat - Centered & Larger */}
            <div className="flex flex-col items-center justify-center mb-4">
              <img 
                src={PEMDA_LOGO_URL} 
                alt="Logo Kabupaten Asmat" 
                className="w-44 h-44 object-contain"
                style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.4))' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <h2 className="font-bold text-xl mt-4 text-center text-white drop-shadow-md">
                Pemerintah Kabupaten Asmat
              </h2>
            </div>
            
            {/* App Logo */}
            <div className="flex items-center gap-3 mt-2">
              <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center">
                <UserCheck className="w-7 h-7 text-indigo-950" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">SIGAP GURU</h1>
                <p className="text-indigo-200 text-sm">Sistem Informasi Kehadiran & Penilaian</p>
              </div>
            </div>
          </div>
          
          <h2 className="text-4xl font-bold mb-6 leading-tight">
            Absensi Modern untuk Pendidikan Asmat
          </h2>
          
          <p className="text-indigo-200 text-lg mb-8">
            Sistem absensi berbasis foto dan GPS yang dapat digunakan bahkan saat jaringan tidak stabil. 
            Untuk Peningkatan Integritas Pendidikan Berkelanjutan.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="font-semibold">Verifikasi Lokasi GPS</h3>
                <p className="text-indigo-200 text-sm">Geo-fencing otomatis berdasarkan koordinat sekolah</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="font-semibold">Mode Offline</h3>
                <p className="text-indigo-200 text-sm">Tetap berfungsi tanpa koneksi internet</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Login Form */}
      <div className="login-form-container flex items-center justify-center p-4 md:p-8 bg-[#fcfbf9]">
        <div className="w-full max-w-md">
          {/* Mobile Logo with Pemda Logo */}
          <div className="lg:hidden flex flex-col items-center gap-4 mb-8">
            {/* Logo Pemda Kabupaten Asmat - Centered & Larger for Mobile */}
            <div className="flex flex-col items-center">
              <img 
                src={PEMDA_LOGO_URL} 
                alt="Logo Kabupaten Asmat" 
                className="w-28 h-28 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <p className="text-indigo-950 font-bold text-base mt-3 text-center">
                Pemerintah Kabupaten Asmat
              </p>
            </div>
            
            {/* App Logo */}
            <div className="flex items-center gap-3 mt-2">
              <div className="w-12 h-12 rounded-xl bg-indigo-950 flex items-center justify-center">
                <UserCheck className="w-7 h-7 text-amber-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-indigo-950">SIGAP GURU</h1>
                <p className="text-gray-500 text-xs">Sistem Kehadiran Guru</p>
              </div>
            </div>
          </div>

          {/* Online Status */}
          {!isOnline && (
            <Alert className="mb-4 border-amber-200 bg-amber-50">
              <AlertDescription className="text-amber-800">
                <strong>Anda sedang offline.</strong>
                <br />
                <span className="text-sm">
                  Login membutuhkan koneksi internet untuk verifikasi keamanan.
                  <br />
                  <em>Tip: Jangan logout saat offline agar bisa tetap menggunakan aplikasi.</em>
                </span>
              </AlertDescription>
            </Alert>
          )}

          <Card className="border-indigo-100/50 shadow-lg">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-indigo-950">Masuk</CardTitle>
              <CardDescription>Gunakan NIP/NUPTK dan password untuk login</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="nip">NIP / NUPTK / Email</Label>
                  <Input
                    id="nip"
                    data-testid="login-nip-input"
                    type="text"
                    placeholder="Masukkan NIP, NUPTK, atau Email"
                    value={nip}
                    onChange={(e) => setNip(e.target.value)}
                    className="h-12 border-indigo-100 focus:border-indigo-500"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type="password"
                    placeholder="Masukkan password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 border-indigo-100 focus:border-indigo-500"
                    required
                    disabled={loading}
                    autoComplete="current-password"
                  />
                </div>

                {/* Cloudflare Turnstile CAPTCHA — hidden on native app */}
                {IS_NATIVE_APP ? (
                  <div className="flex items-center justify-center gap-2 py-2 px-3 bg-emerald-50 rounded-lg border border-emerald-200 w-full" data-testid="native-app-badge">
                    <Smartphone className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs text-emerald-700">Aplikasi Native — Dilindungi Anti-Mock GPS & Face Verification</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <div ref={turnstileRef} data-testid="turnstile-widget" />
                    {turnstileLoaded && turnstileToken && (
                      <div className="flex items-center gap-2 text-xs text-emerald-600">
                        <ShieldCheck className="w-3.5 h-3.5" />
                        <span>Verifikasi CAPTCHA berhasil</span>
                      </div>
                    )}
                    <div className="flex items-center justify-center gap-2 py-2 px-3 bg-slate-50 rounded-lg border border-slate-200 w-full">
                      <Shield className="w-4 h-4 text-slate-500" />
                      <span className="text-xs text-slate-600">Dilindungi oleh Cloudflare Turnstile CAPTCHA</span>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="login-submit-btn"
                  className="w-full h-12 bg-indigo-950 hover:bg-indigo-900 text-white font-semibold"
                  disabled={loading || !isOnline}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Memproses...
                    </>
                  ) : (
                    'Masuk'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-gray-400 mt-6">
            © 2025 SIGAP GURU Dinas Pendidikan Kabupaten Asmat
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
